import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { EN } from './en';

export type Lang = 'it' | 'en';

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  /**
   * Translate an Italian source string. Italian is the source language, so keys
   * ARE the Italian text: `t('Turni')` → 'Shifts' in EN, 'Turni' in IT. Anything
   * not present in the EN dictionary falls back to the Italian source, so a
   * partially-translated app never shows broken keys.
   * Supports `{name}` placeholders: `t('Ciao {name}', { name })`.
   */
  t: (source: string, vars?: Record<string, string | number>) => string;
}

const LangContext = createContext<LangContextValue | null>(null);
const STORAGE_KEY = 'app.lang';

function initialLang(): Lang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'it';
  } catch {
    return 'it';
  }
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable — keep the in-memory choice */
    }
  }, [lang]);

  const value = useMemo<LangContextValue>(
    () => ({
      lang,
      setLang,
      t: (source, vars) => interpolate(lang === 'en' ? EN[source] ?? source : source, vars),
    }),
    [lang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LangProvider');
  return ctx;
}

/** Convenience hook when a component only needs the translate function. */
export function useT(): LangContextValue['t'] {
  return useLang().t;
}
